import { FC, useState, useEffect } from 'react'
import * as React from 'react'
import cx from 'classnames'
import { sample } from 'lodash'

import Styles from './Avatar.module.scss'
import { ReactComponent as LogoIcon } from 'assets/img/almanac-logo.svg'
import { ReactComponent as BlazeLogoIcon } from 'assets/icons/blazeWhiteBg.svg'
import { optimizedImageUrlString } from 'utils'
import { useIsBlaze } from 'hooks/useIsBlaze'
import { RenderedSVGComponent } from 'types'
import { ReactComponent as DocEmoji } from 'assets/icons/document24.svg'

const MAX_IMAGE_SIZE = 500
const AVATAR_COLOR_NAMES = ['green60', 'blue60', 'navy60', 'gray60', 'gold60']
const DEFAULT_AVATAR_COLORS = [
  '#616875',
  '#A3AAB8',
  '#0F4170',
  '#287FBD',
  '#9ED5F2',
  '#006361',
  '#00A39E',
  '#89DCDB',
  '#845210',
  '#EDB62C',
  '#DD4740',
  '#ED847E',
  '#4D2873',
  '#8754BA',
  '#8C2360',
  '#E55CAC',
  '#853B07',
  '#ED7C2C',
]

export interface Props {
  altText?: string
  className?: string
  backgroundColor?: string
  color?: string
  emoji?: string | RenderedSVGComponent
  label?: string
  loading?: boolean
  size?: number
  url?: string | null
  seed?: number
  title?: string
  randomColor?: boolean
  staticColor?: boolean
  onClick?: (e: React.MouseEvent) => void
  workspaceId?: number
}

const MAX_LABEL_LENGTH = 5
const emojiMultiplier = 0.5
const fontSizeMultiplier = [0.625, 0.5, 0.35, 0.275, 0.225]

const AlmanacAvatar: FC<React.PropsWithChildren<Props>> = ({
  label,
  seed,
  url,
  altText,
  className,
  size = 42,
  backgroundColor,
  color,
  randomColor,
  staticColor,
  emoji,
  loading = false,
  title,
  onClick,
}) => {
  const [error, setError] = React.useState(false)
  const optimizedSize = Math.min(size * 2, MAX_IMAGE_SIZE)
  const [avatarColor, setAvatarColor] = useState<string | null>(null)
  const isBlaze = useIsBlaze()

  label = label ?? (typeof emoji === 'string' ? emoji : undefined)
  label = label ? label.slice(0, MAX_LABEL_LENGTH) : ''
  if (url) {
    url = optimizedImageUrlString(url, optimizedSize.toString())
  }

  let content
  if (loading) {
    content = <div className={Styles.loading} />
  } else if (url && !error) {
    content = (
      <img
        onError={(event) => {
          setError(true)
        }}
        src={url}
        alt={`${altText || label} profile`}
        draggable="false"
      />
    )
  } else if (label) {
    content = <span>{label}</span>
  } else if (emoji && typeof emoji !== 'string') {
    content = emoji || <DocEmoji className={Styles.docEmoji} />
  } else if (seed) {
    content = isBlaze ? (
      <BlazeLogoIcon style={{ height: size, width: size }} className={Styles.logo} />
    ) : (
      <LogoIcon
        style={{ height: size, width: size }}
        className={`${Styles.logo} ${Styles[`svg-color-${AVATAR_COLOR_NAMES[seed % AVATAR_COLOR_NAMES.length]}`]}`}
      />
    )
  }

  useEffect(() => {
    if (randomColor) {
      const color = sample(DEFAULT_AVATAR_COLORS) as string
      setAvatarColor(color)
      return
    }

    if (staticColor && !!label) {
      const index = [...label].reduce((acc, _, i) => acc + (label ? label.charCodeAt(i) : 0), 0)
      const color = DEFAULT_AVATAR_COLORS[(index + (seed || 0)) % DEFAULT_AVATAR_COLORS.length] as string
      setAvatarColor(color)
      return
    }

    setAvatarColor(null)
  }, [randomColor, staticColor, label, seed])

  return (
    <div
      className={cx(Styles.avatar, className, {
        [Styles.emoji]: !!emoji,
        [Styles.transparent]: !!url && !error && !emoji,
      })}
      style={{
        height: size,
        width: size,
        minWidth: size,
        fontSize: emoji
          ? size * emojiMultiplier
          : Math.floor(size * fontSizeMultiplier[label.length ? label.length - 1 : 0]),
        backgroundColor: backgroundColor || avatarColor || ((url && !error) || emoji ? color : undefined),
      }}
      title={title}
      onClick={onClick}
      data-name="avatar"
      data-testid="avatar"
    >
      {content}
    </div>
  )
}
export default AlmanacAvatar
